import { CommonModule } from '@angular/common';
import { Component, Inject, Optional } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { AlgorithmUsageGridComponent } from '../../widgets/usage-grid/usage-grid.component';
import { TranslateModule } from '@ngx-translate/core';
import { useLocalization } from '@shared/localization';
import { map, startWith } from 'rxjs/operators';

@Component({
    selector: 'ts-algorithm-deletion-dialog',
    templateUrl: './algorithm-deletion-dialog.component.html',
    styleUrls: ['./algorithm-deletion-dialog.component.scss'],
    imports: [CommonModule, MatDialogModule, MatButtonModule, TranslateModule, AlgorithmUsageGridComponent]
})
export class AlgorithmDeletionDialogComponent {
  constructor(
    @Optional()
    public dialogRef: MatDialogRef<AlgorithmDeletionDialogComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      /**
       * The information to present to the user for deletion warning.
       */
      deletionInformation:
        | {
            pipelineName: string | { [key: string]: string };
            pipelineStepId: string;
          }
        | undefined;

      /**
       * The information to present to the user for deprecation warning.
       */
      deprecationInformation:
        | {
            algorithmName: string;
            isDeprecated: boolean;
          }
        | undefined;

      /**
       * The usage of the algorithm.
       * Should include usage information only for a specific pipeline if the dialog is being used for deletion warning,
       * or all pipelines combined, if the dialog is being used for deprecation warning.
       */
      pipelineUsage: Array<{
        id: string;
        name: string | { [key: string]: string };
        workspaces: Record<string, { id: string; name: string | { [key: string]: string } }>;
      }>;
    }
  ) {}

  public readonly isForDeprecation = this.data.deprecationInformation !== undefined;
  public readonly isAlgorithmDeprecated = this.data.deprecationInformation?.isDeprecated ?? false;

  public readonly currentLanguage$ = useLocalization().currentLanguage$;

  public readonly pipelineName$ = this.currentLanguage$.pipe(
    map((language) => this.resolvePipelineName(language)),
    startWith(this.resolvePipelineName(useLocalization().currentLanguage))
  );

  private resolvePipelineName(language: string): string {
    if (typeof this.data.deletionInformation?.pipelineName === 'string') {
      return this.data.deletionInformation.pipelineName;
    } else {
      return this.data.deletionInformation?.pipelineName[language] ?? '';
    }
  }

  cancel() {
    this.dialogRef?.close(false);
  }

  accept() {
    this.dialogRef?.close(true);
  }
}
